import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AdminLayout } from './components/AdminLayout';
import { API_BASE_URL } from '../../config/apiConfig';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const PageTitle = styled.h1`
  margin: 0;
  color: #2c3e50;
  font-size: 28px;
`;

const RefreshButton = styled.button`
  padding: 10px 20px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #2980b9;
  }
  
  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
  }
`;

const StatusContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const StatusCard = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => {
    if (props.type === 'success') return '#27ae60';
    if (props.type === 'warning') return '#f39c12';
    if (props.type === 'error') return '#e74c3c';
    return '#3498db';
  }};
`;

const StatusTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 18px;
`;

const StatusValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: ${props => {
    if (props.type === 'success') return '#27ae60';
    if (props.type === 'warning') return '#f39c12';
    if (props.type === 'error') return '#e74c3c';
    return '#2c3e50';
  }};
  margin-bottom: 5px;
`;

const StatusLabel = styled.div`
  font-size: 12px;
  color: #7f8c8d;
  text-transform: uppercase;
`;

const ControlsContainer = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`;

const ControlsTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #2c3e50;
`;

const ControlsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
`;

const ControlButton = styled.button`
  padding: 12px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  
  ${props => {
    if (props.variant === 'success') {
      return `
        background-color: #27ae60;
        color: white;
        &:hover { background-color: #229954; }
      `;
    }
    if (props.variant === 'warning') {
      return `
        background-color: #f39c12;
        color: white;
        &:hover { background-color: #e67e22; }
      `;
    }
    if (props.variant === 'danger') {
      return `
        background-color: #e74c3c;
        color: white;
        &:hover { background-color: #c0392b; }
      `;
    }
    return `
      background-color: #3498db;
      color: white;
      &:hover { background-color: #2980b9; }
    `;
  }}
  
  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
  }
`;

const JobsContainer = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const JobsTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #2c3e50;
`;

const JobsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
`;

const JobCard = styled.div`
  padding: 15px;
  border: 1px solid #ecf0f1;
  border-radius: 4px;
  background-color: #f8f9fa;
`;

const JobName = styled.div`
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
`;

const JobStatus = styled.div`
  font-size: 12px;
  color: ${props => props.active ? '#27ae60' : '#e74c3c'};
  text-transform: uppercase;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 10px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid #f5c6cb;
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  margin-top: 15px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #ecf0f1;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-weight: 500;
  color: #7f8c8d;
`;

const InfoValue = styled.span`
  color: #2c3e50;
`;

export default function CronManagement() {
  const [cronStatus, setCronStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchCronStatus = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('admin-token');
      
      const response = await fetch(`${API_BASE_URL}/admin/cron/status`, {
        headers: {
          'Authorization': `${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cron status');
      }

      const data = await response.json();
      setCronStatus(data.data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching cron status:', err);
    } finally {
      setLoading(false);
    }
  };

  const triggerManualCheck = async (mode = 'normal') => {
    try {
      setActionLoading(true);
      const token = sessionStorage.getItem('admin-token');
      
      const response = await fetch(`${API_BASE_URL}/admin/cron/trigger`, {
        method: 'POST',
        headers: {
          'Authorization': `${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mode })
      });

      if (!response.ok) {
        throw new Error('Failed to trigger cron check');
      }

      const data = await response.json();
      setSuccess(`Manual cron check triggered successfully in ${mode} mode`);
      setError(null);
      
      // Refresh status after a short delay
      setTimeout(() => {
        fetchCronStatus();
      }, 2000);
    } catch (err) {
      setError(err.message);
      console.error('Error triggering cron check:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const controlCronService = async (action) => {
    try {
      setActionLoading(true);
      const token = sessionStorage.getItem('admin-token');
      
      const response = await fetch(`${API_BASE_URL}/admin/cron/control`, {
        method: 'POST',
        headers: {
          'Authorization': `${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action })
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action} cron service`);
      }

      const data = await response.json();
      setSuccess(`Cron service ${action}ed successfully`);
      setError(null);
      
      // Refresh status
      setTimeout(() => {
        fetchCronStatus();
      }, 1000);
    } catch (err) {
      setError(err.message);
      console.error(`Error ${action}ing cron service:`, err);
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchCronStatus();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchCronStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const formatUptime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatMemory = (bytes) => {
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  if (loading && !cronStatus) {
    return (
      <AdminLayout>
        <PageHeader>
          <PageTitle>Cron Management</PageTitle>
        </PageHeader>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <LoadingSpinner />
          Loading cron service status...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PageHeader>
        <PageTitle>Cron Management</PageTitle>
        <RefreshButton onClick={fetchCronStatus} disabled={loading}>
          {loading ? <LoadingSpinner /> : '🔄'} Refresh
        </RefreshButton>
      </PageHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <StatusContainer>
        <StatusCard type={cronStatus?.cron_service?.isRunning ? 'error' : 'success'}>
          <StatusTitle>Service Status</StatusTitle>
          <StatusValue type={cronStatus?.cron_service?.isRunning ? 'error' : 'success'}>
            {cronStatus?.cron_service?.isRunning ? 'RUNNING' : 'IDLE'}
          </StatusValue>
          <StatusLabel>Current State</StatusLabel>
        </StatusCard>

        <StatusCard type="info">
          <StatusTitle>Success Rate</StatusTitle>
          <StatusValue>
            {cronStatus?.cron_service?.successCount && cronStatus?.cron_service?.errorCount
              ? ((cronStatus.cron_service.successCount / (cronStatus.cron_service.successCount + cronStatus.cron_service.errorCount)) * 100).toFixed(1) + '%'
              : 'N/A'
            }
          </StatusValue>
          <StatusLabel>
            {cronStatus?.cron_service?.successCount || 0} Success / {cronStatus?.cron_service?.errorCount || 0} Errors
          </StatusLabel>
        </StatusCard>

        <StatusCard type="info">
          <StatusTitle>Last Run</StatusTitle>
          <StatusValue>
            {cronStatus?.cron_service?.lastRunTime 
              ? new Date(cronStatus.cron_service.lastRunTime).toLocaleString()
              : 'Never'
            }
          </StatusValue>
          <StatusLabel>Last Execution</StatusLabel>
        </StatusCard>

        <StatusCard type="info">
          <StatusTitle>Server Uptime</StatusTitle>
          <StatusValue>
            {cronStatus?.uptime ? formatUptime(cronStatus.uptime) : 'N/A'}
          </StatusValue>
          <StatusLabel>System Uptime</StatusLabel>
        </StatusCard>
      </StatusContainer>

      <ControlsContainer>
        <ControlsTitle>Service Controls</ControlsTitle>
        <ControlsGrid>
          <ControlButton 
            onClick={() => triggerManualCheck('normal')}
            disabled={actionLoading}
          >
            {actionLoading ? <LoadingSpinner /> : '▶️'} Trigger Normal Check
          </ControlButton>
          
          <ControlButton 
            onClick={() => triggerManualCheck('light')}
            disabled={actionLoading}
            variant="warning"
          >
            {actionLoading ? <LoadingSpinner /> : '🔆'} Trigger Light Check
          </ControlButton>
          
          <ControlButton 
            onClick={() => controlCronService('restart')}
            disabled={actionLoading}
            variant="success"
          >
            {actionLoading ? <LoadingSpinner /> : '🔄'} Restart Service
          </ControlButton>
          
          <ControlButton 
            onClick={() => controlCronService('stop')}
            disabled={actionLoading}
            variant="danger"
          >
            {actionLoading ? <LoadingSpinner /> : '⏹️'} Stop Service
          </ControlButton>
        </ControlsGrid>
      </ControlsContainer>

      <JobsContainer>
        <JobsTitle>Scheduled Jobs</JobsTitle>
        <JobsList>
          {cronStatus?.cron_service?.jobs?.map((jobName) => (
            <JobCard key={jobName}>
              <JobName>{jobName.replace(/([A-Z])/g, ' $1').trim()}</JobName>
              <JobStatus active={true}>Active</JobStatus>
            </JobCard>
          ))}
        </JobsList>

        <InfoGrid>
          <InfoItem>
            <InfoLabel>Memory Usage:</InfoLabel>
            <InfoValue>
              {cronStatus?.memory_usage?.heapUsed 
                ? formatMemory(cronStatus.memory_usage.heapUsed)
                : 'N/A'
              }
            </InfoValue>
          </InfoItem>
          
          <InfoItem>
            <InfoLabel>Total Memory:</InfoLabel>
            <InfoValue>
              {cronStatus?.memory_usage?.heapTotal 
                ? formatMemory(cronStatus.memory_usage.heapTotal)
                : 'N/A'
              }
            </InfoValue>
          </InfoItem>
          
          <InfoItem>
            <InfoLabel>Server Time:</InfoLabel>
            <InfoValue>
              {cronStatus?.server_time 
                ? new Date(cronStatus.server_time).toLocaleString()
                : 'N/A'
              }
            </InfoValue>
          </InfoItem>
          
          <InfoItem>
            <InfoLabel>Jobs Count:</InfoLabel>
            <InfoValue>
              {cronStatus?.cron_service?.jobs?.length || 0}
            </InfoValue>
          </InfoItem>
        </InfoGrid>
      </JobsContainer>
    </AdminLayout>
  );
} 